import type { Reminder } from "@/api/types";

/**
 * ef-061 anchor (Spec Architect, FROZEN) — the one sentence a reminder row/tile shows about WHEN it is due.
 * Shared by the equipment page (ef-061-8) and the Home reminders tile (ef-061-9) so the two never word it
 * differently; the specs assert these exact phrases. No slice edits this file.
 *
 * Date: `3 days overdue` / `1 day overdue` · `Due today` · `Due in 1 day` / `Due in 5 days`.
 * Hours: `5.5 h past due` · `Due now` (exactly at the figure) · `20 h to go`.
 * With both triggers the more urgent one is worded (a tie goes to the date). A reminder with neither a date
 * nor a reading to count from says `No reading yet`; a completed one-time reminder says `Done`.
 */
type Phrasable = Pick<Reminder, "state" | "daysUntilDue" | "hoursUntilDue">;

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** Up to one decimal, no trailing `.0`, thousands separators: 5.5 -> "5.5", 20 -> "20", 1250.5 -> "1,250.5". */
export function formatHours(hours: number): string {
  return hours.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function datePhrase(days: number): string {
  if (days < 0) return `${plural(-days, "day", "days")} overdue`;
  if (days === 0) return "Due today";
  return `Due in ${plural(days, "day", "days")}`;
}

function hoursPhrase(hours: number): string {
  if (hours < 0) return `${formatHours(-hours)} h past due`;
  if (hours === 0) return "Due now";
  return `${formatHours(hours)} h to go`;
}

/** 0 = due, 1 = soon (within 14 days / 25 hours), 2 = later — the same thresholds the server states use. */
const dateRank = (days: number): number => (days <= 0 ? 0 : days <= 14 ? 1 : 2);
const hoursRank = (hours: number): number => (hours <= 0 ? 0 : hours <= 25 ? 1 : 2);

export function reminderPhrase(r: Phrasable): string {
  if (r.state === "done") return "Done";
  const days = r.daysUntilDue ?? null;
  const hours = r.hoursUntilDue ?? null;
  if (days !== null && hours !== null) return hoursRank(hours) < dateRank(days) ? hoursPhrase(hours) : datePhrase(days);
  if (days !== null) return datePhrase(days);
  if (hours !== null) return hoursPhrase(hours);
  return "No reading yet";
}
